import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth, USER_TIERS } from '../../contexts/AuthContext';
import { useCommunity, POST_TYPES, CATEGORIES } from '../../contexts/CommunityContext';
import { useNotification, NOTIFICATION_TYPES } from '../../contexts/NotificationContext';

const Community = ({ defaultTab = 'announcements' }) => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const {
    getAnnouncements,
    getCommunityPosts,
    getCategoryCount,
    getPostById,
    createPost,
    deletePost,
    addComment,
    deleteComment,
    reportPost,
    reportComment
  } = useCommunity();
  const { addNotification } = useNotification();

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showWriteForm, setShowWriteForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newCategory, setNewCategory] = useState(CATEGORIES.GENERAL);
  const [attachments, setAttachments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [message, setMessage] = useState('');
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [reportReason, setReportReason] = useState('');

  const isAdmin = user?.tier === USER_TIERS.ADMIN;
  const canPost = isAuthenticated && user?.tier !== USER_TIERS.GUEST;

  const announcements = getAnnouncements();
  const communityPosts = getCommunityPosts(selectedCategory);
  const categoryCounts = getCategoryCount();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = [];

    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        newAttachments.push({
          name: file.name,
          type: file.type,
          size: file.size,
          content: reader.result,
        });

        // Update state after the last file is read
        if (newAttachments.length === files.length) {
          setAttachments(prev => [...prev, ...newAttachments]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (fileName) => {
    setAttachments(prev => prev.filter(att => att.name !== fileName));
  };


  const handleCreatePost = (e) => {
    e.preventDefault();
    if (!newTitle.trim() || !newContent.trim()) return;

    const type = activeTab === 'announcements' ? POST_TYPES.ANNOUNCEMENT : POST_TYPES.COMMUNITY;
    createPost(type, newTitle, newContent, user, newCategory, attachments);
    setNewTitle('');
    setNewContent('');
    setNewCategory(CATEGORIES.GENERAL);
    setAttachments([]);
    setShowWriteForm(false);
    setMessage(t('community.success.posted'));
    setTimeout(() => setMessage(''), 3000);
  };

  const handleDeletePost = (postId) => {
    if (window.confirm(t('community.confirm.deletePost'))) {
      try {
        deletePost(postId, user.id, isAdmin);
        setSelectedPost(null);
        setMessage(t('community.success.deleted'));
        setTimeout(() => setMessage(''), 3000);
      } catch (err) {
        setMessage(t('community.error.deleteFailed'));
      }
    }
  };

  const handleAddComment = (e) => {
    e.preventDefault();
    if (!newComment.trim() || !selectedPost) return;

    addComment(selectedPost.id, newComment, user);
    setNewComment('');
    // 댓글 추가 후 선택된 포스트 새로고침
    const updatedPost = getPostById(selectedPost.id);
    setSelectedPost(updatedPost);

    // 글 작성자에게 알림 발송 (자기 글에 댓글 제외)
    if (selectedPost.author.id !== user.id) {
      addNotification(
        selectedPost.author.id,
        NOTIFICATION_TYPES.COMMENT,
        t('notification.messages.newComment', { author: user.name }),
        `"${selectedPost.title}"`,
        `/community`
      );
    }
  };

  const handleDeleteComment = (commentId) => {
    if (window.confirm(t('community.confirm.deleteComment'))) {
      try {
        deleteComment(selectedPost.id, commentId, user.id, isAdmin);
        // 댓글 삭제 후 선택된 포스트 새로고침
        const updatedPost = getPostById(selectedPost.id);
        setSelectedPost(updatedPost);
      } catch (err) {
        setMessage(t('community.error.commentDeleteFailed'));
      }
    }
  };

  const openReportModal = (type, id) => {
    setReportTarget({ type, id });
    setReportReason('');
    setShowReportModal(true);
  };

  const handleReport = () => {
    if (!reportReason.trim()) return;

    try {
      if (reportTarget.type === 'post') {
        reportPost(selectedPost.id, reportReason, user);
      } else {
        reportComment(selectedPost.id, reportTarget.id, reportReason, user);
      }
      setShowReportModal(false);
      setMessage(t('community.success.reported'));
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      if (err.message === 'Already reported') {
        setMessage(t('community.error.alreadyReported'));
      } else {
        setMessage(t('community.error.reportFailed'));
      }
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const currentPosts = activeTab === 'announcements' ? announcements : communityPosts;
  const canWriteInTab = activeTab === 'announcements' ? isAdmin : canPost;

  // Badge styles for categories
  const getCategoryBadgeClasses = (category) => {
    const baseClasses = "inline-block px-2 py-1 text-xs font-medium rounded";
    switch (category) {
      case 'general':
        return `${baseClasses} bg-gray-200 text-gray-600`;
      case 'question':
        return `${baseClasses} bg-amber-100 text-amber-700`;
      case 'discussion':
        return `${baseClasses} bg-blue-50 text-blue-600`;
      case 'showcase':
        return `${baseClasses} bg-emerald-100 text-emerald-700`;
      case 'tip':
        return `${baseClasses} bg-violet-100 text-violet-700`;
      default:
        return `${baseClasses} bg-gray-200 text-gray-600`;
    }
  };

  // 신고 모달
  const ReportModal = () => (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]"
      onClick={() => setShowReportModal(false)}
    >
      <div
        className="bg-white rounded-xl p-8 w-[90%] max-w-[500px]"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="m-0 mb-4 text-xl font-semibold">{t('community.report.title')}</h3>
        <textarea
          value={reportReason}
          onChange={(e) => setReportReason(e.target.value)}
          placeholder={t('community.report.placeholder')}
          rows={4}
          className="w-full px-4 py-3.5 border border-gray-200 rounded-lg text-base font-inherit resize-y mb-4 transition-colors duration-200 focus:outline-none focus:border-gray-800"
        />
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setShowReportModal(false)}
            className="px-6 py-3 bg-transparent text-gray-500 border border-gray-200 rounded-lg text-[0.95rem] font-medium cursor-pointer transition-all duration-200 hover:bg-gray-100"
          >
            {t('community.cancel')}
          </button>
          <button
            onClick={handleReport}
            className="px-6 py-3 bg-[#1d1d1f] text-white border-none rounded-lg text-[0.95rem] font-medium cursor-pointer transition-colors duration-200 hover:opacity-85 disabled:bg-gray-400 disabled:cursor-not-allowed"
            disabled={!reportReason.trim()}
          >
            {t('community.report.submit')}
          </button>
        </div>
      </div>
    </div>
  );

  // 글 상세 보기
  if (selectedPost) {
    return (
      <div className="max-w-[1100px] mx-auto p-8 md:p-4">
        {showReportModal && <ReportModal />}

        <button
          className="inline-flex items-center mb-6 py-2 px-0 bg-none border-none text-gray-500 text-[0.95rem] cursor-pointer transition-colors duration-200 hover:text-[#1d1d1f]"
          onClick={() => setSelectedPost(null)}
        >
          ← {t('community.back')}
        </button>

        {message && (
          <div className={`p-4 rounded-lg mb-6 text-center ${message.includes('error') || message.includes('실패') ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-800'}`}>
            {message}
          </div>
        )}

        <article className="bg-white border border-gray-200 rounded-xl p-8 mb-8 md:p-5">
          <header className="mb-6 pb-4 border-b border-gray-200">
            <div className="flex gap-2 mb-2">
              {selectedPost.type === POST_TYPES.ANNOUNCEMENT && (
                <span className="inline-block px-2 py-1 text-xs font-medium rounded bg-blue-50 text-blue-600">{t('community.badge.announcement')}</span>
              )}
              {selectedPost.category && (
                <span className={getCategoryBadgeClasses(selectedPost.category)}>
                  {t(`community.categories.${selectedPost.category}`)}
                </span>
              )}
            </div>
            <h1 className="text-2xl font-semibold my-2">{selectedPost.title}</h1>
            <div className="flex gap-4 text-sm text-gray-500">
              <span>{selectedPost.author.name}</span>
              <span>{formatDate(selectedPost.createdAt)}</span>
            </div>
          </header>

          <div className="leading-[1.8] text-gray-700 [&>p]:mb-4">
            {selectedPost.content.split('\n').map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>

          {selectedPost.attachments && selectedPost.attachments.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="m-0 mb-4 font-semibold">{t('community.attachments')}</h4>
              <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-4">
                {selectedPost.attachments.map(att => (
                  <div key={att.name}>
                    {att.type.startsWith('image/') ? (
                      <img src={att.content} alt={att.name} className="w-full h-[100px] object-cover rounded-lg" />
                    ) : (
                      <a href={att.content} download={att.name} className="block p-4 bg-gray-50 rounded-lg text-center no-underline text-gray-700 text-sm break-all hover:bg-gray-100">
                        {att.name}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-gray-200 flex gap-2">
            {(selectedPost.author.id === user?.id || isAdmin) && (
              <button
                onClick={() => handleDeletePost(selectedPost.id)}
                className="px-4 py-2 bg-red-100 text-red-600 border-none rounded-md text-[0.85rem] font-medium cursor-pointer transition-all duration-200 hover:bg-red-200"
              >
                {t('community.delete')}
              </button>
            )}
            {canPost && selectedPost.author.id !== user?.id && (
              <button
                onClick={() => openReportModal('post')}
                className="px-4 py-2 bg-orange-50 text-orange-700 border-none rounded-md text-[0.85rem] font-medium cursor-pointer transition-all duration-200 ml-2 hover:bg-orange-100"
              >
                {t('community.report.button')}
              </button>
            )}
          </div>
        </article>

        {/* 댓글 섹션 */}
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-4">{t('community.comments')} ({selectedPost.comments?.length || 0})</h2>

          {canPost && (
            <form onSubmit={handleAddComment} className="mb-6">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={t('community.commentPlaceholder')}
                rows={3}
                className="w-full px-4 py-3.5 border border-gray-200 rounded-lg text-[0.95rem] font-inherit resize-y mb-3 transition-colors duration-200 focus:outline-none focus:border-gray-800"
              />
              <button
                type="submit"
                disabled={!newComment.trim()}
                className="px-5 py-2.5 bg-[#1d1d1f] text-white border-none rounded-md text-sm font-medium cursor-pointer transition-colors duration-200 hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {t('community.submitComment')}
              </button>
            </form>
          )}

          <div className="flex flex-col gap-4">
            {(!selectedPost.comments || selectedPost.comments.length === 0) ? (
              <p className="text-center text-gray-400 p-4">{t('community.noComments')}</p>
            ) : (
              selectedPost.comments.map((comment) => (
                <div key={comment.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex gap-3 mb-2 text-[0.85rem]">
                    <span className="font-medium text-gray-700">{comment.author.name}</span>
                    <span className="text-gray-400">{formatDate(comment.createdAt)}</span>
                  </div>
                  <p className="text-gray-600 leading-relaxed m-0">{comment.content}</p>
                  <div className="flex gap-2 mt-2">
                    {(comment.author.id === user?.id || isAdmin) && (
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        className="mt-2 px-2 py-1 bg-none border-none text-red-600 text-xs cursor-pointer hover:underline"
                      >
                        {t('community.delete')}
                      </button>
                    )}
                    {canPost && comment.author.id !== user?.id && (
                      <button
                        onClick={() => openReportModal('comment', comment.id)}
                        className="px-2 py-1 bg-none border-none text-orange-700 text-xs cursor-pointer hover:underline"
                      >
                        {t('community.report.button')}
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    );
  }

  // 글 목록 보기
  return (
    <div className="max-w-[1100px] mx-auto p-8 md:p-4">
      <header className="mb-8">
        <h1 className="text-[2rem] font-semibold mb-2">{t('community.title')}</h1>
        <p className="text-gray-500">{t('community.subtitle')}</p>
      </header>

      {message && (
        <div className={`p-4 rounded-lg mb-6 text-center ${message.includes('error') || message.includes('실패') ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-800'}`}>
          {message}
        </div>
      )}

      {/* 탭 */}
      <div className="flex gap-2 mb-6 border-b border-gray-200 pb-2">
        <button
          className={`px-6 py-3 bg-none border-none text-base font-medium cursor-pointer rounded-t-lg transition-all duration-200 ${activeTab === 'announcements' ? 'text-gray-900 bg-gray-100' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}
          onClick={() => { setActiveTab('announcements'); setShowWriteForm(false); setSelectedCategory(null); }}
        >
          {t('community.tabs.announcements')}
        </button>
        <button
          className={`px-6 py-3 bg-none border-none text-base font-medium cursor-pointer rounded-t-lg transition-all duration-200 ${activeTab === 'community' ? 'text-gray-900 bg-gray-100' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}
          onClick={() => { setActiveTab('community'); setShowWriteForm(false); }}
        >
          {t('community.tabs.community')}
        </button>
      </div>

      <div className="flex gap-8 md:flex-col">
        {/* 메인 콘텐츠 */}
        <div className="flex-1 min-w-0 order-2 md:order-none">
          {/* 글쓰기 버튼 */}
          {canWriteInTab && !showWriteForm && (
            <button
              className="block mb-6 px-6 py-3 bg-[#1d1d1f] text-white border-none rounded-lg text-base font-medium cursor-pointer transition-all duration-200 hover:opacity-85"
              onClick={() => setShowWriteForm(true)}
            >
              {t('community.write')}
            </button>
          )}

          {/* 글쓰기 폼 */}
          {showWriteForm && (
            <form onSubmit={handleCreatePost} className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
              {activeTab === 'community' && (
                <select
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="w-full px-4 py-3.5 border border-gray-200 rounded-lg text-base font-inherit mb-4 bg-white cursor-pointer focus:outline-none focus:border-gray-800"
                >
                  {Object.values(CATEGORIES).map(cat => (
                    <option key={cat} value={cat}>
                      {t(`community.categories.${cat}`)}
                    </option>
                  ))}
                </select>
              )}
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder={t('community.titlePlaceholder')}
                required
                className="w-full px-4 py-3.5 border border-gray-200 rounded-lg text-base font-inherit mb-4 transition-colors duration-200 focus:outline-none focus:border-gray-900"
              />
              <textarea
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                placeholder={t('community.contentPlaceholder')}
                rows={6}
                required
                className="w-full px-4 py-3.5 border border-gray-200 rounded-lg text-base font-inherit mb-4 resize-y min-h-[120px] transition-colors duration-200 focus:outline-none focus:border-gray-900"
              />

              <div className="my-4">
                <label htmlFor="attachments" className="block font-medium mb-2">{t('community.attachments')}</label>
                <input
                  type="file"
                  id="attachments"
                  multiple
                  onChange={handleFileChange}
                />
              </div>

              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-4">
                  {attachments.map(att => (
                    <div key={att.name} className="flex items-center bg-gray-100 rounded-2xl px-3 py-1 text-[0.85rem]">
                      <span className="max-w-[150px] whitespace-nowrap overflow-hidden text-ellipsis">{att.name}</span>
                      <button type="button" onClick={() => removeAttachment(att.name)} className="bg-none border-none text-xl leading-none ml-2 cursor-pointer text-gray-400 hover:text-gray-700">×</button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowWriteForm(false)}
                  className="px-6 py-3 bg-transparent text-gray-500 border border-gray-200 rounded-lg text-[0.95rem] font-medium cursor-pointer transition-all duration-200 hover:bg-gray-100"
                >
                  {t('community.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-[#1d1d1f] text-white border-none rounded-lg text-[0.95rem] font-medium cursor-pointer transition-colors duration-200 hover:opacity-85"
                >
                  {t('community.submit')}
                </button>
              </div>
            </form>
          )}

          {/* 글 목록 */}
          <div className="flex flex-col gap-4">
            {currentPosts.length === 0 ? (
              <p className="text-center text-gray-400 py-12">{t('community.noPosts')}</p>
            ) : (
              currentPosts.map((post) => (
                <div
                  key={post.id}
                  className="bg-white border border-gray-200 rounded-xl p-5 cursor-pointer transition-all duration-200 hover:border-gray-300 hover:shadow-sm"
                  onClick={() => setSelectedPost(post)}
                >
                  <div className="flex items-center gap-3 mb-2">
                    {post.type === POST_TYPES.ANNOUNCEMENT && (
                      <span className="inline-block px-2 py-1 text-xs font-medium rounded bg-blue-50 text-blue-600">{t('community.badge.announcement')}</span>
                    )}
                    {post.category && (
                      <span className={getCategoryBadgeClasses(post.category)}>
                        {t(`community.categories.${post.category}`)}
                      </span>
                    )}
                    <h3 className="text-lg font-medium m-0">{post.title}</h3>
                  </div>
                  <div className="flex gap-4 text-[0.85rem] text-gray-500 md:flex-wrap md:gap-2">
                    <span>{post.author.name}</span>
                    <span>{formatDate(post.createdAt)}</span>
                    <span>
                      {t('community.commentsCount', { count: post.comments?.length || 0 })}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* 카테고리 사이드바 (커뮤니티 탭에서만 표시) */}
        {activeTab === 'community' && (
          <aside className="w-[220px] flex-shrink-0 order-1 md:w-full md:order-[-1] md:mb-4">
            <h3 className="text-sm font-semibold text-gray-500 mb-4 uppercase tracking-wide">{t('community.categoryTitle')}</h3>
            <ul className="list-none p-0 m-0 md:flex md:flex-wrap md:gap-2">
              <li
                className={`flex justify-between items-center px-4 py-3 rounded-lg cursor-pointer transition-all duration-200 mb-1 md:px-3 md:py-2 md:mb-0 ${selectedCategory === null ? 'bg-[#1d1d1f] text-white' : 'hover:bg-gray-100'}`}
                onClick={() => setSelectedCategory(null)}
              >
                <span>{t('community.categories.all')}</span>
                <span className={`text-xs px-2 py-0.5 rounded-[10px] ${selectedCategory === null ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-500'}`}>{categoryCounts.all}</span>
              </li>
              {Object.values(CATEGORIES).map(cat => (
                <li
                  key={cat}
                  className={`flex justify-between items-center px-4 py-3 rounded-lg cursor-pointer transition-all duration-200 mb-1 md:px-3 md:py-2 md:mb-0 ${selectedCategory === cat ? 'bg-[#1d1d1f] text-white' : 'hover:bg-gray-100'}`}
                  onClick={() => setSelectedCategory(cat)}
                >
                  <span>{t(`community.categories.${cat}`)}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-[10px] ${selectedCategory === cat ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-500'}`}>{categoryCounts[cat] || 0}</span>
                </li>
              ))}
            </ul>
          </aside>
        )}
      </div>
    </div>
  );
};

export default Community;
